import Storage from '@aws-amplify/storage';
import {getTestBlockQuestions} from '@bootcamp/shared/src/util';
import {getQuestionBaseProgresses, getTest} from '@bootcamp/shared/src/requests';

const answerMap = {
  0: 'A',
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
  5: 'F',
  6: 'G',
  7: 'H',
  8: 'I'
};

const compileSubjectTopicCountsForTests = async (testIds) => {
  try {
    const compiledCounts = await testIds.reduce(async (acc, testId) => {
      const test = await getTest(testId);
      const questions = test?.data?.getTest?.blockConnections?.items?.reduce((acc, connection) => ([...acc, ...getTestBlockQuestions(connection?.testBlock)]), [])

      let update = await acc;

      questions.forEach(question => {
        const subjectTagId = question?.tags?.items?.find(({tag}) => !!tag.subject && !tag.topic && !tag.contentType)?.tag?.subject;
        const topicTagId = question?.tags?.items?.find(({tag}) => !!tag.topic)?.tag?.topic;

        update[subjectTagId] = {
          ...(update[subjectTagId] || {}),
          [topicTagId]: update[subjectTagId]?.[topicTagId] ? update[subjectTagId]?.[topicTagId] + 1 : 1,
        };
      });

      return update;
    }, {});

    return compiledCounts

  } catch (error) {
    debugger;
    return {};
  }
}

export const getSubjectTopicCountsForDATPerformance = async (config) => {
  try {
    const existingCountsFetch = await Storage.get(`answerData/datSubjectTopicCounts.json`, {download: true, cacheControl: 'no-cache'});
    const existingCounts = JSON.parse(await existingCountsFetch.Body.text());

    if (existingCounts) {
      return existingCounts;
    }

    const allTests = config
      ?.studyTools
      ?.find(({name}) => name === 'Full Length Tests')
      ?.content
      ?.map(({id}) => id)

    const baseTests = allTests.slice(0, 10);
    const plusTests = allTests.slice(-5);

    const baseCounts = await compileSubjectTopicCountsForTests(baseTests);
    const plusCounts = await compileSubjectTopicCountsForTests(plusTests);

    await Storage.put(`answerData/datSubjectTopicCounts.json`, JSON.stringify({baseCounts, plusCounts}), {
      contentType: 'application/json',
    });

    return {
      baseCounts,
      plusCounts,
    };
  } catch (error) {
    console.log('ERRORING', error);
  }
};

const fetchStats = async (questionId, isNCLEX) => {
  const {data: {getQuestionBase: {progresses}}} = await getQuestionBaseProgresses(questionId, 3000);

  if (progresses?.items) {
    const avgTime = progresses.items.reduce((acc, progress) => {
      try {
        if (!progress.time) return acc;
        const time = parseFloat(progress.time)
        if (time > 500) return acc;
        if (progress.selectedAnswerIndex === -1) return acc;
        return {
          ...acc,
          [progress.questionRevisionId]: {
            time: acc[progress.questionRevisionId]?.time ? acc[progress.questionRevisionId]?.time + time : time,
            total: acc[progress.questionRevisionId]?.total ? acc[progress.questionRevisionId]?.total + 1 : 1
          },
          total: {
            time: acc.total?.time ? acc.total?.time + time : time,
            total: acc.total?.total ? acc.total?.total + 1 : 1
          }
        }
      } catch (error) {
        return acc;
      }
    }, {});

    await Storage.put(`answerData/question/${questionId}-time.json`, JSON.stringify(avgTime), {
      contentType: 'application/json',
    });

    const answerData = progresses.items.reduce((acc, progress) => {
      try {
        if (progress.answerState && isNCLEX) {
          const {answerState, score, maxScore} = JSON.parse(progress.answerState);
          if (!answerState || maxScore > 1) return acc;
          let answerChoice = answerMap[answerState[0]];
          if (score > 0) {
            answerChoice += ' (CORRECT)';
          }
          if (!answerState.length) return acc;
          return {
            ...acc,
            total: {
              ...acc.total,
              [answerChoice]: acc.total?.[answerChoice] ? acc.total?.[answerChoice] + 1 : 1
            },
            [progress.questionRevisionId]: {
              ...(acc[progress.questionRevisionId] || []),
              [answerChoice]: acc?.[progress.questionRevisionId]?.[answerChoice] ? acc?.[progress.questionRevisionId]?.[answerChoice] + 1 : 1
            }
          }
        } else {
          let answerChoice = answerMap[progress.selectedAnswerIndex];
          if (progress.didSelectCorrectAnswer) {
            answerChoice += ' (CORRECT)';
          }
          if (!answerChoice) return acc;
          return {
            ...acc,
            total: {
              ...acc.total,
              [answerChoice]: acc.total?.[answerChoice] ? acc.total?.[answerChoice] + 1 : 1
            },
            [progress.questionRevisionId]: {
              ...(acc[progress.questionRevisionId] || []),
              [answerChoice]: acc?.[progress.questionRevisionId]?.[answerChoice] ? acc?.[progress.questionRevisionId]?.[answerChoice] + 1 : 1
            }
          }
        }
      } catch (error) {
        return acc;
      }
    }, {total: {}});
    await Storage.put(`answerData/question/${questionId}.json`, JSON.stringify(answerData), {
      contentType: 'application/json',
      // progressCallback
    });
    return {answerData, avgTime};
  }
  return {answerData: {}, avgTime: {}};
}

export const fetchAllQuestionStats = async (testId, questions) => {
  try {
    // for flts, this needs to fetch via subject test id instead of storing using flt test id
    //
    try {
      const testAnswerDataFetch = await Storage.get(`answerData/test/${testId}.json`, {download: true, cacheControl: 'no-cache'});
      const testAnswerData = JSON.parse(await testAnswerDataFetch.Body.text());

      return testAnswerData;
    } catch (error) {
      // console.log('ERROR FETCHING TEST DATA');
    }

    // bail if questions aren't passed to this function so we don't store empty array values
    if (!questions?.length) return [];
    const stats = await Promise.all(questions.filter(question => !!question?.question).map(async ({question}) => {
      let answerData, avgTimeTotal;
      try {
        const answerDataFetch = await Storage.get(`answerData/question/${question.id}.json`, {download: true, cacheControl: 'no-cache'});
        answerData = JSON.parse(await answerDataFetch.Body.text());

        const avgTime = await Storage.get(`answerData/question/${question.id}-time.json`, {download: true, cacheControl: 'no-cache'});
        avgTimeTotal = avgTime ? JSON.parse(await avgTime.Body.text()) : null;
      } catch (error) {
        const result = await fetchStats(question.id, false);
        answerData = result.answerData;
        avgTimeTotal = result.avgTime;
      }

      const revisionId = question?.latestRevisions?.items?.[0]?.id;
      const answerDataObject = answerData?.[revisionId] || answerData?.total || {};
      const correctKey = Object.keys(answerDataObject)
        .filter(key => key.includes('CORRECT'))
        .reduce((highestKey, currentKey) => {
          return (answerDataObject[currentKey] || 0) > (answerDataObject[highestKey] || 0) ? currentKey : highestKey;
        }, null);
      const correctCount = answerDataObject?.[correctKey] || 0;
      const totalRecords = Object.values(answerDataObject).reduce((acc, val) => acc + val, 0);

      const totalTime = avgTimeTotal?.total?.time || 0;
      const totalTimeRecords = avgTimeTotal?.total?.total || 0;

      // we need to include topic and subject names here
      return {
        questionId: question.id,
        answerData,
        avgScore: correctCount / totalRecords,
        avgTime: totalTime / totalTimeRecords,
        avgTimeTotal
      };
    }));

    await Storage.put(`answerData/test/${testId}.json`, JSON.stringify(stats), {
      contentType: 'application/json',
    });

    return stats;
  } catch (error) {
    console.log('error fetching question stats', error);
  }
};

export const fetchAllQuestionStatsForFLT = async (questionsByTestId) => {
  try {
    const stats = await Promise.all(Object.entries(questionsByTestId)
      .map(async ([testId, questions]) => {
        try {
          return await fetchAllQuestionStats(testId, questions);
        } catch (error) {
          console.log('error pulling stats for test', testId);
        }
      }));
    return stats;
  } catch (error) {
    console.log('error fetching all stats for FLT', error);
  }
};

// const refetchStats = async (question) => {
//   const isNCLEX = question.tags?.items.some(({tag}) => tag.test === 'NCLEX');
//   await Storage.remove(`answerData/question/${question.id}.json`);
//   await Storage.remove(`answerData/question/${question.id}-time.json`);
//   const {answerData, avgTime} = await fetchStats(question.id, isNCLEX);
//   const updatedQuestionStats = [...questionStats.filter(({questionId}) => questionId !== question.id), {questionId: question.id, answerData, avgTimeTotal: avgTime}];
//   setQuestionStats(updatedQuestionStats);
// }